/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { PublicKey } from '@solana/web3.js';
import useClaim, { ClaimStatusCode } from '../hooks/useClaim';
import { useStyles } from './useStyles';
import { useSolanaWallet } from '../contexts/SolanaWalletContext';
import { ReactComponent as WalletSVG } from '../icons/wallet.svg';
import { ReactComponent as ChicksSVG } from '../icons/chicks.svg';
import { ReactComponent as CalendarSVG } from '../icons/calendar.svg';
import SolanaWalletKey from '../components/SolanaWalletKey';
import ButtonWithLoader from '../components/ButtonWithLoader';
import { isAddress } from '../utils/solanaHelper';
import useClaimInfo from '../hooks/useClaimInfo';
import { displayTime, toDateString, toFixed } from '../utils/helper';
import ShowTx from '../components/ShowTx';
import { CHAIN_ID_SOLANA } from '../lib/consts';
import ConsoleHelper from '../utils/consoleHelper';
import { getRemainingTime, isWithin5Days } from '../utils/claimHelper';
import Information from '../icons/information.svg';

export const ClaimView = () => {
  const classes = useStyles();
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const { publicKey: solanaAddress } = useSolanaWallet();
  const { claimInfoResult, claimInfo } = useClaimInfo();
  const [claimWindowStatus, setClaimWindowStatus] = React.useState<{
    nearestFutureDate: string;
    isWithin5Days: boolean;
  }>();
  const [time, setTime] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nowTs = new Date().getTime();
      setTime(nowTs);
    }, 1000);
  });

  const remainTime = getRemainingTime();
  useEffect(() => {
    if (remainTime === 0) {
      claimInfo();
    }
  }, [remainTime]);

  const { claim, isProcessing, statusCode, lastError, targetTxId } = useClaim();

  const handleClaimButtonClick = () => {
    claim(claimInfoResult.merkleInfo);
  };

  const Icon = styled.div`
    flex: 0 0;
    margin-top: 3px;
  `;

  const TwoCols = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  `;

  const Label = styled.div`
    font-size: 1rem;
    color: #fff;
    opacity: 0.5;
    margin-bottom: 0.25em;
    font-weight: 400;
    text-align: left;
    font-family: 'Bergern', 'Poppins', sans-serif;
  `;

  const Input = styled.input`
    color: #00805b;
    margin: 2.5em 0;
    width: 90%;
    display: block;
    background: transparent;
    padding: 0 16px;
    border: none;
    opacity: 1;
    -webkit-text-fill-color: #fff;
    font-family: 'Bergern', 'Poppins', sans-serif;
    font-size: 1rem;
    ::placeholder {
      color: #00cc92;
    }
    :focus {
      outline: none;
    }
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
  `;

  const InputContainer = styled.div`
    display: flex;
    align-items: center;
    color: #00805b;
    margin: 0.5em 0 0 0;
    background: rgb(22, 31, 38, 0.1);
    height: 60px;
    border-radius: 5px;
    padding: 0 16px;
    border: none;
    opacity: 1;
    -webkit-text-fill-color: rgb(121, 126, 129);
    box-shadow: rgb(42 54 64) 0 0 0 1px;
    font-family: 'Bergern', 'Poppins', sans-serif;
  `;

  const statusMessage = useMemo(() => {
    if (isProcessing || statusCode !== ClaimStatusCode.FAILED) {
      switch (statusCode) {
        case ClaimStatusCode.START:
          return 'Start';
        case ClaimStatusCode.CLAIMING:
          return 'Claiming tokens';
        case ClaimStatusCode.SUBMITTING:
          return 'Submitting result';
        case ClaimStatusCode.SUCCESS:
          return 'Success';
        default:
          return '';
      }
    } else {
      return lastError && lastError > '' ? lastError : 'Unknown error';
    }
  }, [isProcessing, statusCode, lastError]);

  useEffect(() => {
    if (!isProcessing) {
      if (statusCode === ClaimStatusCode.SUCCESS) {
        setSuccessMessage('Success');
        ConsoleHelper(successMessage);
      } else if (statusCode === ClaimStatusCode.FAILED) {
        setErrorMessage(statusMessage);
      }
    }
  }, [isProcessing, statusCode, successMessage, statusMessage]);

  useEffect(() => {
    setClaimWindowStatus(isWithin5Days());
  }, []);

  useEffect(() => {
    if (solanaAddress) {
      claimInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solanaAddress]);

  return (
    <>
      <Container className="container">
        <Card className={classes.mainCard}>
          <div>
            {!claimWindowStatus ||
              (claimWindowStatus && claimWindowStatus.isWithin5Days && (
                <div className={classes.claimAlert}>
                  <img
                    src={Information}
                    alt="information"
                    className={classes.info}
                  />
                  <span className={classes.claimText}>
                    Claims are currently disabled as the dashboard is down for
                    maintenance ahead of the next token unlock. However, you can
                    still view your vesting status. To unlock any unvested
                    tokens, please check on or after{' '}
                    {claimWindowStatus.nearestFutureDate.split('T')[0]}.
                  </span>
                </div>
              ))}
            <Label>Address</Label>
            <InputContainer>
              <Icon>
                <WalletSVG width="20px" height="20px" stroke="white" />
              </Icon>
              <Input
                type="text"
                readOnly
                value={
                  solanaAddress
                    ? solanaAddress.toBase58()
                    : 'Connect your wallet'
                }
                name="address"
              />
            </InputContainer>
            <div
              style={{
                marginTop: '1rem',
                marginBottom: '0.5rem',
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {isAddress(solanaAddress as PublicKey | string) ? (
                <div style={{ paddingRight: '0.5em' }}>
                  <ButtonWithLoader
                    onClick={handleClaimButtonClick}
                    disabled={
                      claimInfoResult.claimableAmount < 0.001 ||
                      !isAddress(solanaAddress as PublicKey | string) ||
                      !claimInfoResult.merkleInfo ||
                      statusMessage === 'Success' ||
                      isProcessing ||
                      !claimWindowStatus ||
                      (claimWindowStatus && claimWindowStatus.isWithin5Days)
                    }
                  >
                    {claimInfoResult.claimableAmount > 0.001 ||
                    claimInfoResult.nextUnlockAmount < 0.001
                      ? `Claim Tokens`
                      : displayTime(remainTime)}
                  </ButtonWithLoader>
                </div>
              ) : null}
              <SolanaWalletKey />
            </div>
            <div>
              Note: You need a one time fee of 0.003 SOL to create your $CHICKS
              token account in your wallet.
            </div>
            <div style={{ marginBottom: '2rem' }}>
              {!errorMessage && statusMessage ? (
                <Typography
                  variant="body2"
                  color="primary"
                  className={classes.statusMessage}
                >
                  {statusMessage}
                </Typography>
              ) : null}
              {targetTxId ? (
                <div style={{ marginTop: '16px' }}>
                  <ShowTx chainId={CHAIN_ID_SOLANA} txId={targetTxId} />
                </div>
              ) : null}
              {errorMessage ? (
                <Typography
                  variant="body2"
                  color="error"
                  className={classes.statusMessage}
                >
                  {errorMessage}
                </Typography>
              ) : null}
            </div>
          </div>
          <TwoCols>
            <div>
              <Label>Total Allocation</Label>
              <InputContainer>
                <Icon>
                  <ChicksSVG width="20px" height="20px" stroke="white" />
                </Icon>
                <Input
                  type="number"
                  readOnly
                  name="totalAllocationAmount"
                  value={
                    claimInfoResult.totalAllocationAmount
                      ? toFixed(claimInfoResult.totalAllocationAmount)
                      : toFixed(0)
                  }
                />
              </InputContainer>
            </div>
            <div>
              <Label>Total Claimed To Date</Label>
              <InputContainer>
                <Icon>
                  <ChicksSVG width="20px" height="20px" stroke="white" />
                </Icon>
                <Input
                  type="number"
                  readOnly
                  name="totalClaimedAmount"
                  value={
                    claimInfoResult.totalClaimedAmount
                      ? toFixed(claimInfoResult.totalClaimedAmount)
                      : toFixed(0)
                  }
                />
              </InputContainer>
            </div>
            <div>
              <Label>Claimable Now</Label>
              <InputContainer>
                <Icon>
                  <ChicksSVG width="20px" height="20px" stroke="white" />
                </Icon>
                <Input
                  type="number"
                  readOnly
                  name="claimableAmount"
                  value={
                    claimInfoResult.claimableAmount
                      ? toFixed(claimInfoResult.claimableAmount)
                      : toFixed(0)
                  }
                />
              </InputContainer>
            </div>
            <div>
              <Label>Unvested</Label>
              <InputContainer>
                <Icon>
                  <ChicksSVG width="20px" height="20px" stroke="white" />
                </Icon>
                <Input
                  type="number"
                  readOnly
                  name="unvestedAmount"
                  value={
                    claimInfoResult.unvestedAmount
                      ? toFixed(claimInfoResult.unvestedAmount)
                      : toFixed(0)
                  }
                />
              </InputContainer>
            </div>
            <div>
              <Label>Next Unlock Date</Label>
              <InputContainer>
                <Icon>
                  <CalendarSVG width="20px" height="20px" stroke="white" />
                </Icon>
                <Input
                  type="string"
                  readOnly
                  name="vested"
                  value={
                    claimInfoResult.nextUnlockDate
                      ? toDateString(claimInfoResult.nextUnlockDate)
                      : 'None'
                  }
                />
              </InputContainer>
            </div>
            <div>
              <Label>Next Unlock Amount</Label>
              <InputContainer>
                <Icon>
                  <ChicksSVG width="20px" height="20px" stroke="white" />
                </Icon>
                <Input
                  type="number"
                  readOnly
                  name="vested"
                  value={
                    claimInfoResult.nextUnlockAmount
                      ? toFixed(claimInfoResult.nextUnlockAmount)
                      : toFixed(0)
                  }
                />
              </InputContainer>
            </div>
          </TwoCols>
        </Card>
      </Container>
    </>
  );
};
